import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';

import { Button } from '@/components/Button/Button';
import { LoadingScreen } from '@/components/LoadingScreen';

import { useAuth } from '@/auth/useAuth';
import { isValidEmail } from '@/features/profile/profileService';
import { useThemeRouteOverride } from '@/theme/useTheme';

import '@/features/marketing/marketing-tokens.css';

import styles from './LoginPage.module.css';
import { getSafeReturnTo, withReturnTo } from './returnTo';

function normalizeEmail(value: string): string {
  return value.trim().toLowerCase();
}

function mapSignupError(error: unknown): string {
  if (
    error &&
    typeof error === 'object' &&
    'code' in error &&
    error.code === 'auth/email-already-in-use'
  ) {
    return 'That email already has an account. Sign in instead.';
  }

  return error instanceof Error && error.message.trim().length > 0
    ? error.message
    : 'Unable to create your account right now. Please try again.';
}

async function missingSignupMethod(): Promise<never> {
  throw new Error('Account creation is not available.');
}

export function SignupPage() {
  useThemeRouteOverride('light');

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = getSafeReturnTo(searchParams.get('returnTo'));
  const {
    createUserWithEmailAndPassword = missingSignupMethod,
    loading,
    user,
  } = useAuth();
  const [email, setEmail] = useState(searchParams.get('email') ?? '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && user && !submitting) {
      navigate(returnTo, { replace: true });
    }
  }, [loading, navigate, returnTo, submitting, user]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const normalizedEmail = normalizeEmail(email);
    setError(null);

    if (!isValidEmail(normalizedEmail)) {
      setError('Enter a valid email address.');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    setSubmitting(true);

    try {
      await createUserWithEmailAndPassword(normalizedEmail, password);
      navigate(withReturnTo('/profile/setup', returnTo), { replace: true });
    } catch (signupError) {
      setError(mapSignupError(signupError));
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return <LoadingScreen message="Preparing signup..." />;
  }

  return (
    <main className={styles.page}>
      <div className={styles.content}>
        <Link className={styles.wordmark} to="/">
          Kudu
        </Link>

        <section aria-labelledby="signup-title" className={styles.card}>
          <div className={styles.cardHeader}>
            <h1 className={styles.title} id="signup-title">
              Create your account
            </h1>
            <p className={styles.description}>
              New Kudu accounts use email and password. Google sign-in is only
              available for existing linked accounts.
            </p>
          </div>

          <form className={styles.form} noValidate onSubmit={handleSubmit}>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="signup-email">
                Email
              </label>
              <input
                aria-describedby={error ? 'signup-error' : undefined}
                aria-invalid={Boolean(error)}
                autoComplete="email"
                className={styles.input}
                id="signup-email"
                onChange={(event) => setEmail(event.target.value)}
                required
                type="email"
                value={email}
              />
            </div>

            <div className={styles.field}>
              <label className={styles.label} htmlFor="signup-password">
                Password
              </label>
              <input
                autoComplete="new-password"
                className={styles.input}
                id="signup-password"
                minLength={6}
                onChange={(event) => setPassword(event.target.value)}
                required
                type="password"
                value={password}
              />
            </div>

            <Button
              className={styles.submitButton}
              disabled={submitting}
              fullWidth
              size="large"
              type="submit"
              variant="primary"
            >
              {submitting ? 'Creating account...' : 'Create account'}
            </Button>
          </form>

          {error ? (
            <p className={styles.error} id="signup-error" role="alert">
              {error}
            </p>
          ) : null}

          <p className={styles.notice}>
            Already have an account?{' '}
            <Link to={withReturnTo('/login', returnTo)}>Sign in</Link>
          </p>
        </section>
      </div>
    </main>
  );
}
